import { ParamMap, Params } from '@angular/router';

import { SearchFormType } from './search-form-type.model';
import { LatLon } from '../../util/geodesy/latlon';
import { QueryValidationReason, QueryValidationResult } from './query-validation-result.model';
import { AirportService, MAX_RADIUS, MIN_RADIUS } from '../../airport/airport.service';
import { BooleanFilter, ENCODED_VALUE, Filter, FilterKeys } from '../filters/filters';
import { ApproachFilter, FuelFilter, RunwayLengthFilter, ServiceFilter, ServiceType } from '../filters/airport-filters';
import { isNullOrUndefined, isNumeric } from '../../util/helpers';
import { AirportSearchResult } from '../../airport/models/airport-search-result.model';
import { ApproachType } from '../../airport/models/approach-type.model';
import { FuelType } from '../../business/models/business-fuel.model';
import { ActivityType } from '../filters/activity-filters';
import { BoundingBox } from '../../util/geodesy/bounding-box';
import { AviationEventFilter } from '../../shared/models/aviation-event-filter.model';
import { formatDate } from '@angular/common';
import * as moment from 'moment-timezone';


/**
 * Regex used for parsing a latitude or a longitude value in decimal degrees (e.g., "-123.4567")
 * @type {RegExp}
 */
export const LATLON_REGEX = /^-?[0-9]+\.[0-9]+$/;


/**
 * Search query model for airports and destinations.
 */
export class SearchQuery {
  /**
   * Search type
   */
  type?: SearchFormType = SearchFormType.Airports;

  /**
   * Query text
   */
  term?: string;

  /**
   * Center point (if performing a radius search)
   */
  location?: LatLon;

  /**
   * Radius (nautical miles)
   */
  radius? = 50;

  /**
   * Location description. Note this is only saved as part of the query so the form field can be restored; it does not
   * have any effect on the search results.
   */
  locationDescription: string;

  /**
   * Bounding box (if performing a search within a rectangular viewport).
   */
  bounds?: BoundingBox;

  /**
   * Two letter US state abbreviation.
   */
  state?: string;

  /**
   * Activity type to search for (only relevant for search type of Activities).
   */
  activity: ActivityType = ActivityType.Restaurants;

  /**
   * Search result filters
   */
  filters: Array<Filter> = [
    new BooleanFilter({
      key: FilterKeys.PublicUse,
      enabled: true,
      apply: (airport: AirportSearchResult) => airport.publicUse
    }),
    new BooleanFilter({
      key: FilterKeys.Towered,
      apply: (airport: AirportSearchResult) => airport.towered
    }),
    new BooleanFilter({
      key: FilterKeys.NoLandingFee,
      apply: (airport: AirportSearchResult) => !airport.landingFee
    }),
    new BooleanFilter({
      key: FilterKeys.CourtesyCar,
      apply: (airport: AirportSearchResult) => airport.courtesyCar
    }),
    new BooleanFilter({
      key: FilterKeys.RentalCar,
      apply: (airport: AirportSearchResult) => airport.rentalCar
    }),
    new BooleanFilter({
      key: FilterKeys.Customs,
      apply: (airport: AirportSearchResult) => airport.customsIntlEntry
    }),
    new BooleanFilter({
      key: FilterKeys.Oxygen,
      apply: (airport: AirportSearchResult) => airport.oxygen
    }),
    new BooleanFilter({
      key: FilterKeys.Restaurant,
      apply: (airport: AirportSearchResult) => airport.restaurantOnField
    }),
    new BooleanFilter({
      key: FilterKeys.Camping,
      apply: (airport: AirportSearchResult) => airport.campingOnField
    }),
    new BooleanFilter({
      key: FilterKeys.PavedRunway,
      apply: (airport: AirportSearchResult) => airport.paved
    }),
    new BooleanFilter({
      key: FilterKeys.Grass,
      apply: (airport: AirportSearchResult) => airport.grass
    }),
    new BooleanFilter({
      key: FilterKeys.Helipad,
      apply: (airport: AirportSearchResult) => airport.helipad
    }),
    new BooleanFilter({
      key: FilterKeys.Waterway,
      apply: (airport: AirportSearchResult) => airport.waterway
    }),
    new RunwayLengthFilter(),
    new FuelFilter(FuelType._100LL),
    new FuelFilter(FuelType.JetA, [FuelType.JetAPremix]),
    new FuelFilter(FuelType.MOGAS),
    new FuelFilter(FuelType.UL94),
    new ApproachFilter(ApproachType.ILS),
    new ApproachFilter(ApproachType.RNAV),
    new ApproachFilter(ApproachType.VOR),
    new ServiceFilter(ServiceType.AirframeRepair),
    new ServiceFilter(ServiceType.EngineRepair),
    new ServiceFilter(ServiceType.AvionicsRepair)
  ];

  aviationEventsFilter?: AviationEventFilter;

   /**
   * Filter map that allows looking up a specific filter (or array of filters, in case of the multi flag being true)
   * for a given filter key. This property is initialized in the constructor.
   */
  private _filterMap: Map<FilterKeys, Filter|Array<Filter>> = new Map<FilterKeys, Filter|Array<Filter>>();

  /**
   * Returns the sanitized query text with all invalid characters removed. This is what will be sent to the API.
   * @returns {string}
   */
  get sanitizedTerm(): string {
    return AirportService.sanitizeSearchTerm(this.term);
  }

  constructor() {
    this.filters.forEach(f => {
      if (f.multi) {
        if (this._filterMap.has(f.key)) {
          (this._filterMap.get(f.key) as Filter[]).push(f);
        } else {
          this._filterMap.set(f.key, [f]);
        }
      } else {
        this._filterMap.set(f.key, f);
      }
    });
  }

  static formatLatLon(loc: LatLon): string {
    const lat = loc.lat.toFixed(7);
    const lon = loc.lon.toFixed(7);
    return `${lat},${lon}`;
  }

  static parseLatLon(loc: string): LatLon {
    if (!loc) {
      return null;
    }
    const parts = loc.split(',');
    if (parts.length !== 2) {
      return null;
    }
    const latstr = parts[0];
    const lonstr = parts[1];
    if (LATLON_REGEX.test(latstr) && LATLON_REGEX.test(lonstr)) {
      try {
        return new LatLon(+latstr, +lonstr);
      } catch (e) {
        console.warn('Error parsing LatLon:', e);
        return null;
      }
    }
    return null;
  }

  static getBooleanValue(params: ParamMap, paramName: string): boolean {
    if (params.has(paramName)) {
      if (params.get(paramName) === 'true') {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  /**
   * Decodes the filters from the given parameter map.
   * @param {ParamMap} params
   */
  static decode(params: ParamMap): SearchQuery {
    const query = new SearchQuery();

    query.term = params.get('q');

    if (params.has('loc') && params.has('radius') && isNumeric(params.get('radius'))) {
      query.location = SearchQuery.parseLatLon(params.get('loc'));
      query.radius = +params.get('radius');
      if (params.has('locstr')) {
        query.locationDescription = params.get('locstr');
      }
    } else if (params.has('ne') && params.has('sw')) {
      const ne = SearchQuery.parseLatLon(params.get('ne'));
      const sw = SearchQuery.parseLatLon(params.get('sw'));
      if (ne && sw) {
        query.bounds = new BoundingBox(ne, sw);
      }
    } else if (params.has('state')) {
      if (params.has('locstr')) {
        query.locationDescription = params.get('locstr');
      }
      query.state = params.get('state');
    }

    if (params.get('t') === 'activities') {
      query.type = SearchFormType.Activities;
      if (params.has('activity')) {
        query.activity = params.get('activity') as ActivityType;
      }
    }

    if (params.get('t') === 'events') {
      query.type = SearchFormType.AviationEvents;
      query.aviationEventsFilter = new AviationEventFilter();
      query.aviationEventsFilter.airshow = SearchQuery.getBooleanValue(params, 'airshow');
      query.aviationEventsFilter.airshowwithmilitarydemonstrationteam = SearchQuery.getBooleanValue(params,
        'airshowwithMilitaryDemonstrationTeam');
      query.aviationEventsFilter.flyin = SearchQuery.getBooleanValue(params, 'flyin');
      query.aviationEventsFilter.flyout = SearchQuery.getBooleanValue(params, 'flyout');
      query.aviationEventsFilter.pancakeBreakfast = SearchQuery.getBooleanValue(params, 'pancakeBreakfast');
      query.aviationEventsFilter.socialGathering = SearchQuery.getBooleanValue(params, 'socialGathering');
      query.aviationEventsFilter.openhouse = SearchQuery.getBooleanValue(params, 'openhouse');
      query.aviationEventsFilter.pokerRun = SearchQuery.getBooleanValue(params, 'pokerRun');
      query.aviationEventsFilter.aviationCommunityDay = SearchQuery.getBooleanValue(params, 'aviationCommunityDay');
      query.aviationEventsFilter.wingsAndWheels = SearchQuery.getBooleanValue(params, 'wingsAndWheels');
      query.aviationEventsFilter.aircraftDisplayRides = SearchQuery.getBooleanValue(params, 'aircraftDisplayRides');
      query.aviationEventsFilter.aopaRustyPilots = SearchQuery.getBooleanValue(params, 'aopaRustyPilots');
      query.aviationEventsFilter.aopaFlyingClub = SearchQuery.getBooleanValue(params, 'aopaFlyingClub');
      query.aviationEventsFilter.safetySeminar = SearchQuery.getBooleanValue(params, 'safetySeminar');
      query.aviationEventsFilter.conference = SearchQuery.getBooleanValue(params, 'conference');
      query.aviationEventsFilter.workshop = SearchQuery.getBooleanValue(params, 'workshop');
      query.aviationEventsFilter.aopaBacktoYourRoots = SearchQuery.getBooleanValue(params, 'aopaBacktoYourRoots');
      query.aviationEventsFilter.formatOnline = SearchQuery.getBooleanValue(params, 'formatOnline');
      query.aviationEventsFilter.formatInPerson = SearchQuery.getBooleanValue(params, 'formatInPerson');
      if (params.has('name')) {
        query.aviationEventsFilter.name = params.get('name');
      }
      if (params.has('startDate')) {
        const tempStartDate = moment(params.get('startDate'));
        query.aviationEventsFilter.startDate = tempStartDate.toDate();
      }
      if (params.has('endDate')) {
        const tempEndDate = moment(params.get('endDate'));
        query.aviationEventsFilter.endDate = tempEndDate.toDate();
      }
    }

    if (params.get('t') === 'fuel') {
      query.type = SearchFormType.FuelPrice;
    }

    query.filters.forEach(filter => {
      let value: ENCODED_VALUE;
      value = filter.multi ? params.getAll(filter.key) : params.get(filter.key);
      filter.enabled = filter.decode(value);
    });
    return query;
  }

  /**
   * Encode the search query as a set of URL parameters
   * @returns {Params}
   */
  encode(): Params {
    const params: Params = {};
    if (this.term) {
      params['q'] = this.term;
    }
    if (this.location && this.radius) {
      params['loc'] = SearchQuery.formatLatLon(this.location);
      params['radius'] = this.radius;
      if (this.locationDescription) {
        params['locstr'] = this.locationDescription;
      }
    } else if (this.bounds) {
      params['ne'] = SearchQuery.formatLatLon(this.bounds.ne);
      params['sw'] = SearchQuery.formatLatLon(this.bounds.sw);
    } else if (this.state) {
      params['state'] = this.state;
      if (this.locationDescription) {
        params['locstr'] = this.locationDescription;
      }
    }
    if (this.type === SearchFormType.Activities) {
      params['t'] = 'activities';
      params['activity'] = this.activity;
    }
    if (this.type === SearchFormType.AviationEvents) {
      params['t'] = 'events';
      params['airshow'] = this.aviationEventsFilter.airshow;
      params['airshowwithMilitaryDemonstrationTeam'] = this.aviationEventsFilter.airshowwithmilitarydemonstrationteam;
      params['flyin'] = this.aviationEventsFilter.flyin;
      params['flyout'] = this.aviationEventsFilter.flyout;
      params['pancakeBreakfast'] = this.aviationEventsFilter.pancakeBreakfast;
      params['socialGathering'] = this.aviationEventsFilter.socialGathering;
      params['openhouse'] = this.aviationEventsFilter.openhouse;
      params['pokerRun'] = this.aviationEventsFilter.pokerRun;
      params['aviationCommunityDay'] = this.aviationEventsFilter.aviationCommunityDay;
      params['wingsAndWheels'] = this.aviationEventsFilter.wingsAndWheels;
      params['aircraftDisplayRides'] = this.aviationEventsFilter.aircraftDisplayRides;
      params['aopaRustyPilots'] = this.aviationEventsFilter.aopaRustyPilots;
      params['aopaFlyingClub'] = this.aviationEventsFilter.aopaFlyingClub;
      params['safetySeminar'] = this.aviationEventsFilter.safetySeminar;
      params['conference'] = this.aviationEventsFilter.conference;
      params['workshop'] = this.aviationEventsFilter.workshop;
      params['aopaBacktoYourRoots'] = this.aviationEventsFilter.aopaBacktoYourRoots;
      params['formatOnline'] = this.aviationEventsFilter.formatOnline;
      params['formatInPerson'] = this.aviationEventsFilter.formatInPerson;
      params['name'] = this.aviationEventsFilter.name;
      params['startDate'] = moment(this.aviationEventsFilter.startDate).local().format('YYYY-MM-DD');
      params['endDate'] = moment(this.aviationEventsFilter.endDate).local().format('YYYY-MM-DD');
    }
    if (this.type === SearchFormType.FuelPrice) {
      params['t'] = 'fuel';
    }

    this.filters
      .filter(f => f.enabled && f.valid)
      .forEach(filter => {
        if (filter.multi) {
          const param = params[filter.key] || [];
          const value = filter.encode();
          if (!isNullOrUndefined(value)) {
            param.push(value);
            params[filter.key] = param;
          }
        } else {
          const value = filter.encode();
          if (!isNullOrUndefined(value)) {
            params[filter.key] = value;
          }
        }
      });
    return params;
  }

  /**
   * Returns a filter (or array of filters, if the multi flag is true) for a given filter key.
   * @param {FilterKeys} key
   * @returns {Filter | Array<Filter>}
   */
  getFilter(key: FilterKeys): Filter|Array<Filter> {
    return this._filterMap.get(key);
  }

  /**
   * Validates the search query to ensure a minimum amount of information has been entered to execute a search, and
   * that no conflicting parameters were entered.
   * @returns {QueryValidationResult}
   */
  validate(): QueryValidationResult {
    const term = this.sanitizedTerm;

    // Validate search type
    if (!Object.values(SearchFormType).includes(this.type)) {
      return {
        valid: false,
        reason: QueryValidationReason.INVALID_SEARCH_TYPE
      };
    }

    // Validate we either have query text, a lat/lon, or bounding box
    // allow searching for US events without a search term
    if ( (((!term || term.length < 3) && this.type !== SearchFormType.AviationEvents) ||
      ((!term || term.length < 3) && (this.type === SearchFormType.AviationEvents && this.aviationEventsFilter
        && this.aviationEventsFilter.international ))) && !this.location && !this.bounds && !this.state) {
      return {
        valid: false,
        reason: QueryValidationReason.MISSING_BASE_PARAMS,
        message: 'Please enter at least three characters or select a valid location to initiate a search.'
      };
    }

    if (((term && term.length > 0 && this.type === SearchFormType.AviationEvents) ||
      (term && term.length > 0 && this.type === SearchFormType.FuelPrice)) && !this.location && !this.bounds && !this.state) {
      return {
        valid: false,
        reason: QueryValidationReason.MISSING_BASE_PARAMS,
        message: 'Please choose a search location from the dropdown list.'
      };
    }

    // If doing an activity search, location or bounding box is required
    if (this.type === SearchFormType.Activities && !this.location && !this.bounds) {
      return {
        valid: false,
        reason: QueryValidationReason.MISSING_BASE_PARAMS,
        message: 'Please select a location from the dropdown to initiate an activity search.'
      };
    }

    // If we have a lat/lon, distance is required
    if (this.location && !this.radius) {
      return {
        valid: false,
        reason: QueryValidationReason.MISSING_RADIUS
      };
    }

    // Validate radius is in range
    if (this.type === SearchFormType.AviationEvents && this.radius && (this.radius < MIN_RADIUS || this.radius > 500)) {
      return {
        valid: false,
        reason: QueryValidationReason.INVALID_RADIUS,
        message: `Distance must be between ${MIN_RADIUS} and ${MAX_RADIUS} nautical miles.`
      };
    }

    if (this.type !== SearchFormType.AviationEvents && this.radius && (this.radius < MIN_RADIUS || this.radius > MAX_RADIUS)) {
      return {
        valid: false,
        reason: QueryValidationReason.INVALID_RADIUS,
        message: `Distance must be between ${MIN_RADIUS} and ${MAX_RADIUS} nautical miles.`
      };
    }

    // Ensure all filters are valid
    const invalid = this.filters
      .filter(f => f.enabled)
      .map(f => f.validate())
      .filter(r => !r.valid);
    if (invalid.length) {
      return invalid[0];
    }

    return { valid: true };
  }

  /**
   * Execute the query against a list of airport search results.
   * @param airports {Array<AirportSearchResult>} Airport search results to filter
   * @returns {AirportSearchResult[]} Airport search results that match the filters
   */
  execute(airports: Array<AirportSearchResult>): Array<AirportSearchResult> {
    return airports.filter(airport => {
      return this.filters.every(filter => filter.apply(airport));
    });
  }
}
